import type { OutreachOverviewData, V2GetPortfolioQueueResponse } from '@workspace/api-backend'
import { cn } from '@workspace/ui/lib/utils'

interface Props {
  funnel: OutreachOverviewData['funnel']
}

interface Stage {
  key: keyof OutreachOverviewData['funnel']
  label: string
  description: string
}

const STAGES: Stage[] = [
  {
    key: 'queued',
    label: 'Queued',
    description: 'Investors matched to your thesis, waiting to be contacted.',
  },
  {
    key: 'sent',
    label: 'Leads pushed',
    description: 'Lead handed to HeyReach, warming up before first contact.',
  },
  {
    key: 'reachedOut',
    label: 'Reached out',
    description: 'Connection request sent or first message delivered.',
  },
  {
    key: 'profileViewed',
    label: 'Profile viewed',
    description: 'Investor opened your profile via the outreach link.',
  },
  {
    key: 'accepted',
    label: 'Accepted',
    description: 'Investor accepted the connection, and is now in your network.',
  },
  {
    key: 'replied',
    label: 'Replied',
    description: 'Investor sent at least one message back.',
  },
  {
    key: 'callBooked',
    label: 'Call booked',
    description: 'Admin-confirmed intro call.',
  },
]

type PortfolioQueueData = Extract<V2GetPortfolioQueueResponse, { status: 'ok' }>['data']

interface PortfolioFunnelProps {
  queue: PortfolioQueueData | null
}

const PORTFOLIO_STAGES = [
  { label: 'Queued', description: 'Founders researched and queued for outreach.' },
  {
    label: 'Leads pushed',
    description: 'Lead handed to HeyReach, warming up before first contact.',
  },
  { label: 'Reached out', description: 'Connection request sent or first message delivered.' },
  { label: 'Accepted', description: 'Founder accepted the connection request.' },
  { label: 'Replied', description: 'Founder sent at least one message back.' },
  { label: 'Warm intro offered', description: 'Founder agreed to introduce you to their fund.' },
]

export function PortfolioOutreachFunnel({ queue }: PortfolioFunnelProps) {
  const all = queue ? [...queue.queued, ...queue.active, ...queue.warmIntro, ...queue.finished] : []
  const counts = [
    all.length,
    all.filter((m) => m.pushedAt != null).length,
    all.filter((m) => m.connectionRequestSentAt != null).length,
    all.filter((m) => m.connectionAcceptedAt != null).length,
    all.filter((m) => m.firstReplyAt != null).length,
    all.filter((m) => m.warmIntroCandidate).length,
  ]
  const topCount = Math.max(...counts, 0)

  return (
    <div className="rounded-2xl border border-border/60 bg-card/40 p-6">
      <div className="mb-5 flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-foreground">Funnel</h2>
        <p className="text-xs text-muted-foreground">From queued founders to warm intros offered</p>
      </div>

      <div className="space-y-2.5">
        {PORTFOLIO_STAGES.map((stage, index) => {
          const count = counts[index] ?? 0
          const prev = index > 0 ? (counts[index - 1] ?? 0) : null
          const widthPct = topCount > 0 ? Math.max(8, (count / topCount) * 100) : 0
          const showConversion = index > 0
          const conversionPct =
            showConversion && prev && prev > 0 ? Math.round((count / prev) * 100) : null

          return (
            <div key={stage.label}>
              <div className="mb-1 flex items-baseline justify-between gap-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-foreground">{stage.label}</span>
                  <span className="font-mono text-sm font-semibold text-foreground">{count}</span>
                </div>
                {conversionPct !== null && (
                  <span
                    className={cn(
                      'rounded-md px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
                      conversionPct >= 50
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : conversionPct >= 25
                          ? 'bg-amber-500/10 text-amber-400'
                          : 'bg-muted/40 text-muted-foreground'
                    )}
                  >
                    {conversionPct}% from prev
                  </span>
                )}
              </div>
              <div className="relative h-7 overflow-hidden rounded-md bg-muted/20">
                <div
                  className={cn(
                    'h-full rounded-md transition-all',
                    index === PORTFOLIO_STAGES.length - 1
                      ? 'bg-emerald-500/30'
                      : index === 0
                        ? 'bg-primary/30'
                        : 'bg-primary/20'
                  )}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground/80">{stage.description}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function OutreachFunnel({ funnel }: Props) {
  const topCount = Math.max(...STAGES.map((s) => funnel[s.key]), 0)

  return (
    <div className="rounded-2xl border border-border/60 bg-card/40 p-6">
      <div className="mb-5 flex items-baseline justify-between">
        <h2 className="text-base font-semibold text-foreground">Funnel</h2>
        <p className="text-xs text-muted-foreground">
          From queued investors to confirmed intro calls
        </p>
      </div>

      <div className="space-y-2.5">
        {STAGES.map((stage, index) => {
          const count = funnel[stage.key]
          const prevStage = index > 0 ? STAGES[index - 1] : undefined
          const prev = prevStage ? funnel[prevStage.key] : null
          const widthPct = topCount > 0 ? Math.max(8, (count / topCount) * 100) : 0
          // Skip the Queued → Sent conversion: Queued is the remaining backlog,
          // not the funnel's top — `sent / queued` produces nonsense like 2400%.
          const showConversion = stage.key !== 'sent'
          const conversionPct =
            showConversion && prev && prev > 0 ? Math.round((count / prev) * 100) : null

          return (
            <div key={stage.key}>
              <div className="mb-1 flex items-baseline justify-between gap-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-foreground">{stage.label}</span>
                  <span className="font-mono text-sm font-semibold text-foreground">{count}</span>
                </div>
                {conversionPct !== null && (
                  <span
                    className={cn(
                      'rounded-md px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
                      conversionPct >= 50
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : conversionPct >= 25
                          ? 'bg-amber-500/10 text-amber-400'
                          : 'bg-muted/40 text-muted-foreground'
                    )}
                  >
                    {conversionPct}% from prev
                  </span>
                )}
              </div>
              <div className="relative h-7 overflow-hidden rounded-md bg-muted/20">
                <div
                  className={cn(
                    'h-full rounded-md transition-all',
                    index === STAGES.length - 1
                      ? 'bg-emerald-500/30'
                      : index === 0
                        ? 'bg-primary/30'
                        : 'bg-primary/20'
                  )}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground/80">{stage.description}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
